import {
  api,
  ApiError,
  type AttachmentKind,
  type MessageDto,
  type OutgoingAlbum,
  type OutgoingItem,
  type OutgoingMedia,
  type OutgoingMessage,
  type ReplyTo,
} from '../api/client.ts';
import { BLOB_STORE, hasIdb, idbDelete, idbGet, idbGetAll, idbPut, OUTBOX_STORE } from '../lib/idb.ts';
import {
  chunkFrom,
  nextPartIndex,
  resumeFrom,
  retryDelayMs,
  sendProgress,
  UPLOAD_CHUNK_BYTES,
} from '../lib/outbox.ts';
import { canCompressVideo, compressAlbumVideos } from '../lib/videoCompress.ts';
import {
  cancel as cancelBackground,
  handOver,
  running as backgroundRunning,
  supportsBackgroundFetch,
  type BackgroundFetchRegistration,
} from './backgroundFetch.ts';

/**
 * The queue of things this device is sending, which is deliberately not part of
 * any screen.
 *
 * A send used to belong to the conversation it was started in: the upload was a
 * promise held by a React callback, the bubble was a row in that thread's state,
 * and both of them assumed the sender would sit and watch. Phones do not work
 * that way. Locking one, or switching to the camera to answer a question about
 * the photo being sent, suspends the page — and re-entering the chat reloaded
 * the thread from the server, which knew nothing about a message still going
 * up, so the bubble disappeared while the upload it was drawing carried on
 * invisibly behind it.
 *
 * So a send lives here instead: in a module, not a component; in IndexedDB, not
 * memory; addressed by the sender's own id, so the same send can be recognised
 * after the app has been closed and opened again. Three things follow from that,
 * and they are the point of the whole file.
 *
 * - Leaving the chat, or the app, does not interrupt anything. Nothing is
 *   cancelled on unmount because nothing is owned by a mount.
 * - A send that was killed mid-upload — the tab discarded, the phone out of
 *   memory, the browser closed — resumes from the byte it reached, because the
 *   bytes are chunked against a server-side session that remembers (uploads.ts).
 * - Opening the conversation again shows the send where it actually is, because
 *   the bubble is rendered from this queue rather than from the thread.
 *
 * What it cannot do on its own is make progress while the page is frozen. That
 * needs the browser's help, and only some browsers offer it — see
 * backgroundFetch.ts, which takes over the same queue where it can.
 */

/** Which multipart field a part stands in for once it reaches the server. */
type PartRole = 'file' | 'poster' | 'media' | 'mediaPoster';

interface StoredPart {
  role: PartRole;
  /** Album parts only: which tile this is, or which tile a poster belongs to. */
  index: number;
  fileName: string;
  mime: string;
  size: number;
  /** Album media only, so the re-encode knows what it is looking at. */
  attachmentKind?: AttachmentKind;
  /** The server-side session these bytes go into; null until one is opened. */
  uploadId: string | null;
  /** How many of its bytes the server has confirmed. */
  offset: number;
}

/**
 * A queued send, as it is written down. Everything here survives the app being
 * closed; the blobs live beside it in their own store, keyed by blobKey().
 */
interface StoredEntry {
  clientId: string;
  chatId: string;
  createdAt: number;
  /** The finalize's fields, bar the uploads — those are built from `parts`. */
  message: Omit<OutgoingMessage, 'uploads' | 'posterFor' | 'clientId'>;
  parts: StoredPart[];
  /**
   * Whether the album's videos still have to be re-encoded on this device.
   *
   * Persisted, and set to false only once it has actually happened, so a phone
   * that dies during a transcode does the transcode again rather than uploading
   * the 50 МБ original it was in the middle of shrinking.
   */
  needsCompression: boolean;
  /** The bubble to draw, as JSON. Nothing in here is an object URL. */
  draft: MessageDto;
  /** Album tiles, as the bubble draws them; parallel to the album's items. */
  tiles: { kind: AttachmentKind; durationMs: number | null }[];
  /**
   * The finalize, ready to post — written down as soon as every part has a
   * session, and null until then.
   *
   * It is stored rather than rebuilt because of who else reads this record: the
   * service worker, woken by a completed background fetch with no page running
   * and no reason to know how a message is assembled. Keeping the payload here
   * is what lets that handler be four lines of "post this to that" instead of a
   * second copy of finalize() living in a worker.
   */
  finalize: OutgoingMessage | null;
}

/** A queued send with the parts of it that only make sense while running. */
interface LiveEntry extends StoredEntry {
  phase: 'compressing' | 'uploading';
  status: 'sending' | 'failed';
  /** 0–1 across whichever phase it is in. */
  progress: number;
  /** Consecutive failures, which is what the backoff is computed from. */
  attempts: number;
  /** Not before this. Set by a failure, cleared by a manual retry. */
  retryAt: number;
  /** Object URLs for the album tiles; this module owns and revokes them. */
  previews: (string | null)[];
  /**
   * The browser is uploading this one itself, with the page frozen or gone.
   * While it is set, nothing here touches those sessions — see backgroundFetch.
   */
  background: BackgroundFetchRegistration | null;
  /** How many bytes were already up when the browser took it over. */
  backgroundBase: number;
  /** Cancels the request in flight, so a handover has one writer, not two. */
  abort: AbortController | null;
}

/** What a conversation draws for a send that has not landed yet. */
export interface PendingMessage extends MessageDto {
  status: 'sending' | 'failed';
  progressPct: number;
  sendPhase: 'compressing' | 'uploading';
  localItems?: { previewUrl: string | null; kind: AttachmentKind; durationMs: number | null }[];
}

const entries = new Map<string, LiveEntry>();
const blobs = new Map<string, Blob>();

type Listener = () => void;
type SettleListener = (message: MessageDto, clientId: string) => void;

const listeners = new Set<Listener>();
const settleListeners = new Set<SettleListener>();

/** How long a send that never succeeded is kept before it is given up on. */
const KEEP_FOR_MS = 24 * 60 * 60 * 1000;

function blobKey(clientId: string, part: number): string {
  return `${clientId}#${part}`;
}

// ---- notification ----

/**
 * Throttled, because the two things that drive it are a video encoder and a
 * chunked upload, and the encoder reports many times a second. A bubble's
 * percentage is not worth a re-render of a thread at that rate.
 */
let lastNotify = 0;
let notifyTimer: number | undefined;
const NOTIFY_EVERY_MS = 120;

function notify(immediate = false): void {
  if (notifyTimer !== undefined) {
    window.clearTimeout(notifyTimer);
    notifyTimer = undefined;
  }
  const wait = immediate ? 0 : Math.max(0, NOTIFY_EVERY_MS - (Date.now() - lastNotify));
  if (wait === 0) {
    lastNotify = Date.now();
    for (const listener of listeners) listener();
    return;
  }
  notifyTimer = window.setTimeout(() => {
    notifyTimer = undefined;
    lastNotify = Date.now();
    for (const listener of listeners) listener();
  }, wait);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Called with the real message once a send lands, so the thread can swap the
 * pending bubble for it. Fires for a resumed send too, in whatever screen
 * happens to be open — including none of them.
 */
export function onSettled(listener: SettleListener): () => void {
  settleListeners.add(listener);
  return () => settleListeners.delete(listener);
}

// ---- reading the queue ----

/**
 * Every send still in the queue, across every conversation, oldest first.
 *
 * All of them rather than one chat's, because the caller holds this as state
 * and re-reads it when the queue moves: a send finishing in a conversation
 * nobody is looking at still has to leave that list.
 */
export function snapshot(): PendingMessage[] {
  return [...entries.values()]
    .sort((left, right) => left.createdAt - right.createdAt)
    .map(toPending);
}

function toPending(entry: LiveEntry): PendingMessage {
  return {
    ...entry.draft,
    status: entry.status,
    progressPct: Math.round(entry.progress * 100),
    sendPhase: entry.phase,
    ...(entry.tiles.length > 0
      ? {
          localItems: entry.tiles.map((tile, index) => ({
            previewUrl: entry.previews[index] ?? null,
            kind: tile.kind,
            durationMs: tile.durationMs,
          })),
        }
      : {}),
  };
}

// ---- persistence ----

async function persist(entry: LiveEntry): Promise<void> {
  if (!hasIdb()) return;
  const stored: StoredEntry = {
    clientId: entry.clientId,
    chatId: entry.chatId,
    createdAt: entry.createdAt,
    message: entry.message,
    parts: entry.parts,
    needsCompression: entry.needsCompression,
    draft: entry.draft,
    tiles: entry.tiles,
    finalize: finalizePayload(entry),
  };
  try {
    await idbPut(OUTBOX_STORE, entry.clientId, stored);
  } catch {
    // A full disk, or a private window with no database. The send carries on
    // from memory — it just will not survive the app being closed, which is
    // exactly where this app was before any of this existed.
  }
}

async function forget(entry: LiveEntry): Promise<void> {
  for (const url of entry.previews) if (url) URL.revokeObjectURL(url);
  entries.delete(entry.clientId);
  for (let index = 0; index < entry.parts.length; index += 1) {
    blobs.delete(blobKey(entry.clientId, index));
  }
  if (!hasIdb()) return;
  try {
    await idbDelete(OUTBOX_STORE, entry.clientId);
    for (let index = 0; index < entry.parts.length; index += 1) {
      await idbDelete(BLOB_STORE, blobKey(entry.clientId, index));
    }
  } catch {
    // Left behind at worst; the next start() sweeps anything too old.
  }
}

/**
 * The bytes of one part, from memory if this session put them there and from
 * the database if a previous one did.
 *
 * Cached in the map on the way past, because a chunked upload asks for the same
 * blob once per chunk and reading fifty megabytes back out of IndexedDB twenty
 * times would be the slowest thing in the send.
 */
async function blobFor(clientId: string, index: number): Promise<Blob | null> {
  const key = blobKey(clientId, index);
  const held = blobs.get(key);
  if (held) return held;
  if (!hasIdb()) return null;
  try {
    const found = await idbGet<Blob>(BLOB_STORE, key);
    if (found) blobs.set(key, found);
    return found ?? null;
  } catch {
    return null;
  }
}

// ---- putting things in ----

interface Enqueued {
  chatId: string;
  clientId: string;
  draft: MessageDto;
  message: StoredEntry['message'];
  parts: { role: PartRole; index: number; blob: Blob; fileName: string; attachmentKind?: AttachmentKind }[];
  needsCompression?: boolean;
  tiles?: StoredEntry['tiles'];
  previews?: (string | null)[];
}

async function enqueue(input: Enqueued): Promise<void> {
  const entry: LiveEntry = {
    clientId: input.clientId,
    chatId: input.chatId,
    createdAt: input.draft.createdAt,
    message: input.message,
    parts: input.parts.map((part) => ({
      role: part.role,
      index: part.index,
      fileName: part.fileName,
      mime: part.blob.type || 'application/octet-stream',
      size: part.blob.size,
      ...(part.attachmentKind ? { attachmentKind: part.attachmentKind } : {}),
      uploadId: null,
      offset: 0,
    })),
    needsCompression: input.needsCompression ?? false,
    draft: input.draft,
    tiles: input.tiles ?? [],
    finalize: null,
    phase: input.needsCompression ? 'compressing' : 'uploading',
    status: 'sending',
    progress: 0,
    attempts: 0,
    retryAt: 0,
    previews: input.previews ?? [],
    background: null,
    backgroundBase: 0,
    abort: null,
  };

  entries.set(entry.clientId, entry);
  input.parts.forEach((part, index) => blobs.set(blobKey(entry.clientId, index), part.blob));
  notify(true);

  // The bytes go down before the metadata, so a record that exists always has
  // its files. The reverse would leave a resumed send pointing at nothing.
  if (hasIdb()) {
    try {
      for (const [index, part] of input.parts.entries()) {
        await idbPut(BLOB_STORE, blobKey(entry.clientId, index), part.blob);
      }
    } catch {
      // Same as persist(): it sends, it just does not survive a reload.
    }
  }
  await persist(entry);
  void pump();
}

/**
 * The two fields a reply puts on the wire. Flat rather than nested because this
 * object is also what sits in IndexedDB until the queue drains: an entry written
 * by an older build has no `replyQuote` and must still send.
 */
function replyFields(replyTo: ReplyTo | null) {
  return { replyToId: replyTo?.id ?? null, replyQuote: replyTo?.quote ?? null };
}

/** A document or a picture chosen from the file picker. */
export function queueFile(
  chatId: string,
  clientId: string,
  file: File,
  replyTo: ReplyTo | null,
  draft: MessageDto,
): Promise<void> {
  return enqueue({
    chatId,
    clientId,
    draft,
    message: replyFields(replyTo),
    parts: [{ role: 'file', index: 0, blob: file, fileName: file.name }],
  });
}

/** A кружочек or a voice message, already encoded by capture.ts. */
export function queueMedia(
  chatId: string,
  clientId: string,
  media: OutgoingMedia,
  replyTo: ReplyTo | null,
  draft: MessageDto,
): Promise<void> {
  return enqueue({
    chatId,
    clientId,
    draft,
    message: {
      ...replyFields(replyTo),
      kind: media.kind,
      // Stated as a field as well as on the session: the server checks both
      // against the same allowlist, and a recording's type carries a codec list
      // that not every layer between here and there handles gracefully.
      mime: media.blob.type,
      durationMs: media.durationMs,
      ...(media.trimStartMs !== null ? { trimStartMs: media.trimStartMs } : {}),
      ...(media.trimEndMs !== null ? { trimEndMs: media.trimEndMs } : {}),
    },
    parts: [
      { role: 'file', index: 0, blob: media.blob, fileName: media.fileName },
      ...(media.poster
        ? [{ role: 'poster' as const, index: 0, blob: media.poster, fileName: 'poster.jpg' }]
        : []),
    ],
  });
}

/** A group of photos and videos, as one message. */
export function queueAlbum(
  chatId: string,
  clientId: string,
  album: OutgoingAlbum,
  replyTo: ReplyTo | null,
  draft: MessageDto,
): Promise<void> {
  const caption = album.caption.trim();
  return enqueue({
    chatId,
    clientId,
    draft,
    message: {
      ...replyFields(replyTo),
      ...(caption ? { body: caption } : {}),
      mediaDurationMs: album.items.map((item) => String(item.durationMs ?? '')),
    },
    parts: [
      ...album.items.map((item, index) => ({
        role: 'media' as const,
        index,
        blob: item.file,
        fileName: item.file.name,
        attachmentKind: item.kind,
      })),
      ...album.items.flatMap((item, index) =>
        item.poster
          ? [{ role: 'mediaPoster' as const, index, blob: item.poster, fileName: 'poster.jpg' }]
          : [],
      ),
    ],
    // Decided here rather than inside the encoder, because it is what the first
    // frame of the bubble says: a group of photographs must not flash «Сжимаем».
    needsCompression: canCompressVideo() && album.items.some((item) => item.kind === 'video'),
    tiles: album.items.map((item) => ({ kind: item.kind, durationMs: item.durationMs })),
    previews: album.items.map((item) => item.previewUrl),
  });
}

// ---- taking things out ----

/** Gives up on a send and throws its bytes away. Nothing is sent afterwards. */
export async function discard(clientId: string): Promise<void> {
  const entry = entries.get(clientId);
  if (!entry) return;
  entry.abort?.abort();
  await cancelBackground(clientId);
  await forget(entry);
  notify(true);
}

/**
 * Re-checks the queue against the server and gets it moving again.
 *
 * For when something outside this module has changed what the server holds —
 * the service worker having finished a handed-over upload, above all. Each
 * entry re-reads its own sessions on the next attempt, so this needs to know
 * nothing about what happened.
 */
export async function resume(): Promise<void> {
  await reclaim();
  await pump();
}

/** Tries a failed send again now, rather than waiting for the backoff. */
export function retry(clientId: string): void {
  const entry = entries.get(clientId);
  if (!entry) return;
  entry.status = 'sending';
  entry.attempts = 0;
  entry.retryAt = 0;
  notify(true);
  void pump();
}

// ---- the pump ----

/** The run in progress, so a handover can wait for it to stop writing. */
let pumping: Promise<void> | null = null;
let wakeTimer: number | undefined;

/**
 * Runs queued sends one at a time, forever, in the order they were made.
 *
 * One at a time on purpose. Two uploads over one mobile uplink do not arrive
 * sooner than two in a row; they arrive at the same time as each other, which
 * turns two bars that finish into two bars that crawl. The queue is also the
 * order the sender chose, and a chat where the second photo can overtake the
 * first is a chat that reorders itself.
 */
async function drain(): Promise<void> {
  for (;;) {
    const now = Date.now();
    const next = [...entries.values()]
      .filter(
        (entry) => entry.status === 'sending' && entry.retryAt <= now && entry.background === null,
      )
      .sort((left, right) => left.createdAt - right.createdAt)[0];

    if (!next) {
      scheduleWake();
      return;
    }
    await runEntry(next);
  }
}

/**
 * The flag is cleared from `.finally` rather than from inside drain(), and that
 * is not a style choice.
 *
 * A run that finds nothing to do never awaits, so it finishes *synchronously* —
 * before the assignment below has happened. Clearing the flag in its own body
 * would therefore clear it first and set it second, leaving `pumping` pointing
 * at a promise that has already settled and every later call short-circuiting
 * on it forever. The queue would then hold a message with a spinner on it and
 * never send a byte, which is exactly what it did. A `.finally` callback always
 * runs in a later microtask, so by the time it fires both names are bound.
 */
function pump(): Promise<void> {
  if (pumping) return pumping;
  const run = drain().finally(() => {
    if (pumping === run) pumping = null;
  });
  pumping = run;
  return run;
}

/** A timer for the next entry whose backoff has not elapsed yet. */
function scheduleWake(): void {
  if (wakeTimer !== undefined) {
    window.clearTimeout(wakeTimer);
    wakeTimer = undefined;
  }
  const waiting = [...entries.values()]
    .filter((entry) => entry.status === 'sending' && entry.retryAt > Date.now())
    .map((entry) => entry.retryAt)
    .sort((left, right) => left - right)[0];
  if (waiting === undefined) return;
  wakeTimer = window.setTimeout(() => {
    wakeTimer = undefined;
    void pump();
  }, Math.max(0, waiting - Date.now()));
}

/**
 * Whether a failure is worth waiting out.
 *
 * Anything the network did — no answer at all, or a server fault — will very
 * likely work in a minute, and that is what this whole mechanism is for. A 4xx
 * is the server having read the request and refused it: the file is too big,
 * the chat is gone, the session has expired. Retrying those is a queue that
 * never empties and a bubble that never resolves, so they stop.
 */
function worthRetrying(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status >= 500;
}

async function runEntry(entry: LiveEntry): Promise<void> {
  try {
    if (entry.needsCompression) await compress(entry);
    const message = await upload(entry);

    for (const listener of settleListeners) listener(message, entry.clientId);
    await forget(entry);
    notify(true);
  } catch (error) {
    entry.attempts += 1;
    if (worthRetrying(error)) {
      entry.retryAt = Date.now() + retryDelayMs(entry.attempts);
    } else {
      entry.status = 'failed';
      entry.retryAt = 0;
    }
    notify(true);
  }
}

/**
 * Re-encodes the album's videos, in place in the queue.
 *
 * The result is written back over the originals — blob store and part sizes
 * both — so that a send interrupted after the transcode never does it twice,
 * and never falls back to uploading the file that was picked. Those are the
 * same 50 МБ this exists to avoid sending.
 */
async function compress(entry: LiveEntry): Promise<void> {
  entry.phase = 'compressing';
  entry.progress = 0;
  notify(true);

  const mediaParts = entry.parts
    .map((part, at) => ({ part, at }))
    .filter(({ part }) => part.role === 'media');

  const items: OutgoingItem[] = [];
  for (const { part, at } of mediaParts) {
    const blob = await blobFor(entry.clientId, at);
    if (!blob) throw new ApiError('Файл потерян', 400);
    items.push({
      file: new File([blob], part.fileName, { type: part.mime }),
      kind: part.attachmentKind ?? 'photo',
      // The encoder reads none of these and passes them through untouched; the
      // tile's own picture is held by the entry, not by the item.
      poster: null,
      durationMs: null,
      previewUrl: null,
    });
  }

  const ready = await compressAlbumVideos(items, (fraction) => {
    entry.progress = fraction;
    notify();
  });

  for (const [index, { part, at }] of mediaParts.entries()) {
    const file = ready[index]!.file;
    if (file === items[index]!.file) continue;
    blobs.set(blobKey(entry.clientId, at), file);
    part.fileName = file.name;
    part.mime = file.type;
    part.size = file.size;
    // An offset from a previous attempt describes bytes that no longer exist.
    part.uploadId = null;
    part.offset = 0;
    if (hasIdb()) {
      try {
        await idbPut(BLOB_STORE, blobKey(entry.clientId, at), file);
      } catch {
        // In memory only from here; the send still works, a reload loses it.
      }
    }
  }

  entry.needsCompression = false;
  // Reset before the upload rather than letting it run on from 100: the two
  // bars measure different things and share one number.
  entry.phase = 'uploading';
  entry.progress = 0;
  await persist(entry);
  notify(true);
}

/**
 * Sends every part that is not already up, then asks for the message.
 *
 * Each part is resumed against the server's own count before a byte of it is
 * re-sent — see resumeFrom for why the server's number wins even when it is
 * lower than this device's.
 */
async function upload(entry: LiveEntry): Promise<MessageDto> {
  entry.phase = 'uploading';
  notify();

  for (;;) {
    const index = nextPartIndex(entry.parts);
    if (index === -1) break;
    await uploadPart(entry, index);
  }

  return finalize(entry);
}

async function uploadPart(entry: LiveEntry, index: number): Promise<void> {
  const part = entry.parts[index]!;

  if (part.uploadId !== null) {
    const remote = await api.uploadStatus(part.uploadId);
    const decision = resumeFrom(remote, part.size);
    if (decision.action === 'restart') {
      part.uploadId = null;
      part.offset = 0;
    } else if (decision.action === 'finalize') {
      // These bytes are already a message. Nothing more to send for this part;
      // the finalize below will be answered with the message that owns it.
      part.offset = part.size;
      return;
    } else {
      part.offset = decision.offset;
    }
  }

  if (part.uploadId === null) {
    const ticket = await api.beginUpload(entry.chatId, {
      fileName: part.fileName,
      mime: part.mime,
      totalBytes: part.size,
    });
    part.uploadId = ticket.id;
    part.offset = ticket.offset;
    await persist(entry);
  }

  const blob = await blobFor(entry.clientId, index);
  if (!blob) throw new ApiError('Файл потерян', 400);

  for (;;) {
    const range = chunkFrom(part.offset, part.size, UPLOAD_CHUNK_BYTES);
    if (range === null) break;

    entry.abort = new AbortController();
    let result;
    try {
      result = await api.sendChunk(
        part.uploadId,
        range.start,
        blob.slice(range.start, range.end),
        entry.abort.signal,
      );
    } finally {
      entry.abort = null;
    }
    // A 409 is the server correcting this device rather than a failure: the
    // chunk it is refusing has already landed. Taking its offset is what turns
    // a lost response into a no-op instead of a duplicated slice.
    part.offset = result.offset;
    entry.progress = sendProgress(entry.parts);
    await persist(entry);
    notify();
  }
}

/**
 * The message these uploads become, or null while any part still has no
 * session to name.
 *
 * Kept separate from posting it because it is also written into the record for
 * the service worker to post — see StoredEntry.finalize.
 */
function finalizePayload(entry: StoredEntry): OutgoingMessage | null {
  if (entry.parts.some((part) => part.uploadId === null)) return null;

  const byRole = (role: PartRole) => entry.parts.filter((part) => part.role === role);
  const idOf = (part: StoredPart) => part.uploadId!;

  const file = byRole('file')[0];
  const poster = byRole('poster')[0];
  const media = byRole('media');
  const posters = byRole('mediaPoster');

  return {
    ...entry.message,
    clientId: entry.clientId,
    uploads: {
      ...(file ? { file: idOf(file) } : {}),
      ...(poster ? { poster: idOf(poster) } : {}),
      ...(media.length > 0 ? { media: media.map(idOf) } : {}),
      ...(posters.length > 0 ? { mediaPoster: posters.map(idOf) } : {}),
    },
    // Which tile each poster belongs to, stated rather than inferred: a video
    // whose frame could not be grabbed sends no poster, and pairing by position
    // would hand its neighbour's picture to every video after it.
    ...(posters.length > 0 ? { posterFor: posters.map((part) => part.index) } : {}),
  };
}

async function finalize(entry: LiveEntry): Promise<MessageDto> {
  const payload = finalizePayload(entry);
  if (!payload) throw new ApiError('Загрузка не завершена', 400);
  return api.postMessage(entry.chatId, payload);
}

// ---- the browser's turn ----

/**
 * Hands every queued send to the browser, for as long as the app is not on
 * screen.
 *
 * Called when the page is hidden, which is the moment before it may be frozen
 * and never resumed — the phone locking, the app switcher, a tap on a
 * notification from something else. From here on this module cannot be relied
 * on to run at all, so anything that can be given away is.
 *
 * The request in flight is aborted first and the pump is waited out, so that
 * the browser's upload and this one never write to the same session at once.
 * Both would be writing identical bytes to identical offsets, so nothing could
 * be corrupted by it — but the two would race to report where they had got to,
 * and a session whose count went backwards would re-send a tail for no reason.
 *
 * Everything here is best-effort. On a browser without Background Fetch —
 * which is every iPhone — nothing happens, and the send resumes when the app is
 * opened again, from the byte it reached.
 */
async function handOverAll(): Promise<void> {
  if (!supportsBackgroundFetch()) return;
  const queued = [...entries.values()].filter(
    (entry) => entry.status === 'sending' && entry.background === null && !entry.needsCompression,
  );
  if (queued.length === 0) return;

  for (const entry of queued) entry.abort?.abort();
  await pumping?.catch(() => {});

  for (const entry of queued) {
    // Still worth doing? The pump may have finished it on the way out.
    if (!entries.has(entry.clientId) || entry.background !== null) continue;

    try {
      // Every part needs a session before the browser can be given a URL to
      // write into. Opening them is the one thing that must happen while there
      // is still a page, and it is why this is worth awaiting rather than
      // firing off: a handover that is one round trip short is not a handover.
      for (const part of entry.parts) {
        if (part.uploadId !== null) continue;
        const ticket = await api.beginUpload(entry.chatId, {
          fileName: part.fileName,
          mime: part.mime,
          totalBytes: part.size,
        });
        part.uploadId = ticket.id;
        part.offset = ticket.offset;
      }
      await persist(entry);

      const parts = [];
      for (const [index, part] of entry.parts.entries()) {
        const blob = await blobFor(entry.clientId, index);
        if (!blob) continue;
        parts.push({ uploadId: part.uploadId!, offset: part.offset, size: part.size, blob });
      }

      const handle = await handOver(entry.clientId, parts, backgroundTitle(entry));
      if (!handle) continue;
      entry.background = handle;
      entry.backgroundBase = entry.parts.reduce((sum, part) => sum + part.offset, 0);
      watchBackground(entry, handle);
    } catch {
      // Offline, most likely — there is nothing to hand over to a browser that
      // cannot reach the server either. It resumes here when the app comes back.
    }
  }

  // Whatever the browser would not take stays this module's problem, and hidden
  // is not frozen: a backgrounded tab usually goes on running for minutes, and
  // those minutes are how far an upload gets on every browser that has no
  // Background Fetch at all. The backoff from the chunk aborted above is
  // cleared, because it was cancelled rather than failed.
  let remaining = false;
  for (const entry of entries.values()) {
    if (entry.status !== 'sending' || entry.background !== null) continue;
    entry.retryAt = 0;
    remaining = true;
  }
  if (remaining) void pump();
}

/** What the notification in the shade says while the browser is uploading. */
function backgroundTitle(entry: LiveEntry): string {
  if (entry.draft.kind === 'album') {
    const count = entry.tiles.length;
    return count > 1 ? `Отправка ${count} файлов` : 'Отправка файла';
  }
  return entry.draft.kind === 'videoNote' || entry.draft.kind === 'voice'
    ? 'Отправка записи'
    : 'Отправка файла';
}

/**
 * Keeps the bubble's percentage moving while the browser has the upload.
 *
 * `uploaded` counts only what this handover was given, so the bytes that were
 * already up before it started are added back — otherwise a send taken over at
 * 80% would appear to start again from nothing.
 */
function watchBackground(entry: LiveEntry, handle: BackgroundFetchRegistration): void {
  const total = entry.parts.reduce((sum, part) => sum + part.size, 0);
  handle.addEventListener('progress', () => {
    if (entry.background !== handle) return;
    if (total > 0) {
      entry.progress = Math.min(1, (entry.backgroundBase + handle.uploaded) / total);
      notify();
    }
    if (handle.result === '') return;
    // Finished, one way or the other. Which way is not decided here: the parts
    // are re-read from the server on the next attempt, and that is the only
    // account of what actually landed that is worth believing.
    entry.background = null;
    void pump();
  });
}

/**
 * Takes back anything the browser has finished with, and leaves alone anything
 * it is still doing.
 *
 * Run when the app comes back on screen. A handover that is still going is left
 * to finish — it is doing the same work this would, without the page having to
 * stay open — and its progress is watched again, because the listener from the
 * last session went away with the page.
 */
async function reclaim(): Promise<void> {
  if (!supportsBackgroundFetch()) return;
  for (const entry of entries.values()) {
    const handle = await backgroundRunning(entry.clientId);
    if (handle) {
      if (entry.background !== handle) {
        entry.background = handle;
        entry.backgroundBase = entry.parts.reduce((sum, part) => sum + part.offset, 0);
        watchBackground(entry, handle);
      }
      continue;
    }
    entry.background = null;
  }
}

// ---- coming back ----

let started = false;

/**
 * Picks the queue up where the last session left it, and keeps it moving.
 *
 * Called once the app knows who is logged in — which is also the only moment
 * the resumed uploads could be authorised, since a session is only ever as
 * privileged as the account that opened it.
 */
export async function start(): Promise<void> {
  if (started) return;
  started = true;

  window.addEventListener('online', () => {
    // Every backoff is cancelled: the thing they were all waiting for has
    // happened, and making a send that is ready sit out another 60 seconds is
    // the difference between "it resumed when I got signal" and "it resumed
    // eventually".
    for (const entry of entries.values()) if (entry.status === 'sending') entry.retryAt = 0;
    void pump();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void reclaim().then(() => pump());
      return;
    }
    // Going away. Whatever can be given to the browser is given to it now,
    // because after this there may be no more turns.
    void handOverAll();
  });

  if (hasIdb()) {
    try {
      const stored = await idbGetAll<StoredEntry>(OUTBOX_STORE);
      const cutoff = Date.now() - KEEP_FOR_MS;
      for (const record of stored) {
        if (record.createdAt < cutoff) {
          // Old enough that the server's own sessions have been swept, and old
          // enough that resuming it would put yesterday's photo in today's
          // conversation without anybody asking for it.
          await idbDelete(OUTBOX_STORE, record.clientId);
          for (let index = 0; index < record.parts.length; index += 1) {
            await idbDelete(BLOB_STORE, blobKey(record.clientId, index));
          }
          continue;
        }
        if (entries.has(record.clientId)) continue;
        entries.set(record.clientId, await revive(record));
      }
    } catch {
      // No database, or it would not open. Sends made in this session still
      // work; the ones from last time are not recoverable.
    }
  }

  notify(true);
  void pump();
}

/**
 * A stored record as something that can run again.
 *
 * The tiles' pictures are made afresh from the blobs, because an object URL
 * does not survive the page that created it — and a resumed album with no
 * previews would be the nine grey squares this app went out of its way to
 * avoid the first time.
 */
async function revive(record: StoredEntry): Promise<LiveEntry> {
  const previews: (string | null)[] = record.tiles.map(() => null);
  for (const [index, part] of record.parts.entries()) {
    if (part.role !== 'media' && part.role !== 'mediaPoster') continue;
    // A video's tile draws its grabbed frame; a photo's draws itself. Where
    // there is both, the poster wins, exactly as the live path arranges it.
    const isPoster = part.role === 'mediaPoster';
    if (!isPoster && record.tiles[part.index]?.kind === 'video') continue;
    const blob = await blobFor(record.clientId, index);
    if (blob) previews[part.index] = URL.createObjectURL(blob);
  }

  return {
    ...record,
    phase: record.needsCompression ? 'compressing' : 'uploading',
    status: 'sending',
    progress: record.needsCompression ? 0 : sendProgress(record.parts),
    attempts: 0,
    retryAt: 0,
    previews,
    background: null,
    backgroundBase: 0,
    abort: null,
  };
}
